import { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { Type } from "@themes/pollinator/ui/Type";
import { InnerContentFrame } from "@themes/pollinator/ui/InnerContentFrame";

export type Props = {
  /** Panel title */
  title: string;
  /** Optional description */
  description?: string;
  /** Children to render below the title */
  children?: ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Whether to use inverted colors */
  isInverted?: boolean;
  /** Additional CSS classes for inner content */
  innerClassName?: string;
};

/**
 * A panel title component with title, description, and optional children.
 * Used for section headers in layouts.
 */
export function PanelTitle({
  className,
  title,
  description,
  children,
  isInverted,
  innerClassName,
}: Props) {
  return (
    <InnerContentFrame
      className={twMerge(
        "w-full py-4 gap-12 bg-brand-50 text-brand-800 border-b border-brand-800 md:text-left md:flex-1 md:border-none md:bg-brand-800 md:text-brand-50 md:justify-start md:max-h-screen md:overflow-y-hidden",
        isInverted && "bg-brand-800 text-brand-50",
        className
      )}
      innerClassName={twMerge("gap-8 px-4", innerClassName)}
    >
      <div className="flex flex-col text-center gap-4 md:gap-8 md:text-left">
        <Type variant="h1">{title}</Type>
        {description && (
          <Type
            variant="body1"
            className={twMerge(
              "text-brand-800 md:text-brand-100",
              isInverted && "text-brand-100"
            )}
          >
            {description}
          </Type>
        )}
      </div>
      {children}
    </InnerContentFrame>
  );
}

export default PanelTitle;

